import { useState, useEffect, useCallback } from 'react';
import { API } from '@/utils/API';

export interface TimelineTimestamps {
  submitted_at?: string;
  pre_review_at?: string;
  review_at?: string;
  post_review_at?: string;
  complete_at?: string;
}

interface UseSubmissionTimelineReturn {
  timelineData: TimelineTimestamps | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  clearError: () => void;
}

export const useSubmissionTimeline = (
  submissionId: string | null
): UseSubmissionTimelineReturn => {
  const [timelineData, setTimelineData] = useState<TimelineTimestamps | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTimeline = useCallback(async () => {
    if (!submissionId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await API.authenticated.fetch(
        `/submissions/${submissionId}/timeline`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch submission timeline');
      }

      const data: TimelineTimestamps = await response.json();
      setTimelineData(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to load submission timeline'
      );
    } finally {
      setIsLoading(false);
    }
  }, [submissionId]);

  useEffect(() => {
    if (submissionId) {
      fetchTimeline();
    }
  }, [submissionId, fetchTimeline]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    timelineData,
    isLoading,
    error,
    refetch: fetchTimeline,
    clearError,
  };
};
